/**
 * A soft, dismissible nudge for a registered player who hasn't set a profile birth
 * year (FR-2.8). Shows only when signed in, no year on file, and not already
 * dismissed — and NEVER blocks play: "Not now" hides it and is remembered so we don't
 * re-nag on every navigation. "Set it" stashes the current location so the profile
 * step can resume the player back to their seat.
 *
 * Reads the persisted identity + dismissal directly (synchronous, no Cognito round
 * trip): a guest never matches (no `hasBirthYear`), so the banner is inert for them.
 */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  dismissBirthYearPrompt,
  isBirthYearPromptDismissed,
  loadIdentity,
  stashReturnTo,
} from '../net/auth';

export function BirthYearPrompt(): React.JSX.Element | null {
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => isBirthYearPromptDismissed());

  const identity = loadIdentity();
  const registered = identity !== null && !identity.isGuest;
  if (!registered || identity.hasBirthYear === true || dismissed) return null;

  const goToProfile = (): void => {
    stashReturnTo(location.pathname + location.search);
    void navigate('/profile');
  };
  const notNow = (): void => {
    dismissBirthYearPrompt();
    setDismissed(true);
  };

  return (
    <div className="birth-year-prompt" data-testid="birth-year-prompt">
      <span className="birth-year-prompt__text">
        Set your birth year for age-appropriate games?
      </span>
      <div className="birth-year-prompt__actions">
        <button
          type="button"
          className="btn btn--primary"
          data-testid="birth-year-prompt-set"
          onClick={goToProfile}
        >
          Set it
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          data-testid="birth-year-prompt-dismiss"
          onClick={notNow}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
